import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import { get } from "../../../../utils/api";
import { fixedTons, formatNumberCommaSeparated } from "../../../../utils/common";
import PieChart from "../../../../Components/GraphNCharts/Pie";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import { FormControl, MenuItem, Select } from "@mui/material";

const Dashboard = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({ month: 4, year: currentYear - 1 });
  const [toDate, setToDate] = useState({
    month: 3,
    year: currentYear,
  });
  const [dashboardData, setDashboardData] = useState({
    totalScope1Emissions: 0,
    totalScope2Emissions: 0,
    totalScope3Emissions: 0,
    totalFranchises: 0,
  });
  const [franchiseRecords, setFranchiseRecords] = useState([]);
  const [franchiseTypeCounts, setFranchiseTypeCounts] = useState([]);
  const [selectedEmissionScope, setSelectedEmissionScope] = useState("scope1");

  const getNumeric = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const getScopeTotalFromCardData = (cardData = {}, scopeNumber) => {
    const scopeLower = `scope${scopeNumber}`;
    const entries = Object.entries(cardData || {});

    // Pick the best matching numeric key for this scope (robust to backend naming differences)
    let best = { score: -1, value: 0 };

    for (const [key, value] of entries) {
      const keyLower = String(key).toLowerCase();
      if (!keyLower.includes(scopeLower)) continue;

      const numericValue = getNumeric(value);
      if (numericValue === 0) {
        // still allow zero, but only if we haven't found any better match yet
        if (best.score !== -1) continue;
      }

      let score = 0;
      if (keyLower.includes("total")) score += 3;
      if (keyLower.includes("emission")) score += 2;
      if (keyLower.includes("metric")) score += 1;

      if (score > best.score) {
        best = { score, value: numericValue };
      }
    }

    // Fallback to some common names if scanning didn't find anything
    const fallbacks = [
      `totalScope${scopeNumber}Emissions`,
      `totalScope${scopeNumber}Emission`,
      `totalScope${scopeNumber}EmissionsMetricTon`,
      `totalScope${scopeNumber}EmissionMetricTon`,
      `scope${scopeNumber}EmissionsMetricTon`,
      `scope${scopeNumber}EmissionMetricTon`,
    ];

    for (const fb of fallbacks) {
      if (Object.prototype.hasOwnProperty.call(cardData, fb)) {
        const v = getNumeric(cardData[fb]);
        if (v !== 0) return v;
      }
    }

    return best.value || 0;
  };

  const yearList = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year += 1) years.push(year);
    return years;
  }, [currentYear]);

  const fetchCardData = async () => {
    try {
      const start = new Date(fromDate.year, fromDate.month - 1, 1).toISOString();
      const end = new Date(toDate.year, toDate.month, 0).toISOString();
      const query = new URLSearchParams({
        page: "1",
        limit: "1000",
        sortField: "createdAt",
        sortOrder: "desc",
        startDate: start,
        endDate: end,
      }).toString();

      const [res, resList] = await Promise.all([
        get(`/scope3/franchises/cardData`),
        get(`/scope3/franchises/get?${query}`).catch(() => ({ data: { data: [] } })),
      ]);
      const data = res?.data?.data || {};
      const list = resList?.data?.data || [];
      setFranchiseRecords(list);
      const groupedCounts = list.reduce((acc, item) => {
        const type = String(item?.franchiseType || "").trim() || "Unknown";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      const categoryCounts = Object.entries(groupedCounts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);
      setFranchiseTypeCounts(categoryCounts);
      const totalScope1Emissions = getScopeTotalFromCardData(data, 1);
      const totalScope2Emissions = getScopeTotalFromCardData(data, 2);
      const totalScope3Emissions = getScopeTotalFromCardData(data, 3);
      const totalFranchisesFromCardData = getNumeric(data?.totalEntries);

      // If cardData doesn't include usable values, fall back to summing from franchise records.
      const computedTotal = totalScope1Emissions + totalScope2Emissions + totalScope3Emissions;
      if (computedTotal === 0) {
        const hasFromTo = fromDate?.year && fromDate?.month && toDate?.year && toDate?.month;
        if (hasFromTo) {
          const sumScope = (fieldName) =>
            list.reduce((acc, item) => acc + getNumeric(item?.[fieldName]), 0);

          const summed1 = sumScope("scope1EmissionsMetricTon");
          const summed2 = sumScope("scope2EmissionsMetricTon");
          const summed3 = sumScope("scope3EmissionsMetricTon");

          setDashboardData({
            totalScope1Emissions: summed1,
            totalScope2Emissions: summed2,
            totalScope3Emissions: summed3,
            totalFranchises: list.length,
          });
          return;
        }
      }

      setDashboardData({
        totalScope1Emissions,
        totalScope2Emissions,
        totalScope3Emissions,
        totalFranchises: totalFranchisesFromCardData,
      });
    } catch (error) {
      console.error("Error fetching franchises dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, [fromDate, toDate, selectedFacility]);

  const totalEmission =
    dashboardData.totalScope1Emissions +
    dashboardData.totalScope2Emissions +
    dashboardData.totalScope3Emissions;

  const hasData = totalEmission > 0;

  const scopePercentageRows = [
    {
      label: "Scope 3",
      value: dashboardData.totalScope3Emissions,
      color: "#F8B400",
    },
    {
      label: "Scope 2",
      value: dashboardData.totalScope2Emissions,
      color: "#FC4A4A",
    },
    {
      label: "Scope 1",
      value: dashboardData.totalScope1Emissions,
      color: "#0B5ED7",
    },
  ].map((item) => ({
    ...item,
    percentage:
      totalEmission > 0 ? Math.round((item.value / totalEmission) * 100) : 0,
  }));

  const visibleScopePercentageRows = hasData
    ? scopePercentageRows.filter((item) => item.value > 0)
    : [];

  const pieChartConfig = useMemo(() => {
    const items = [
      {
        label: "Scope 1",
        value: dashboardData.totalScope1Emissions,
        color: "#0051B7",
      },
      {
        label: "Scope 2",
        value: dashboardData.totalScope2Emissions,
        color: "#FF4444",
      },
      {
        label: "Scope 3",
        value: dashboardData.totalScope3Emissions,
        color: "#FFBB33",
      },
    ].filter((item) => item.value > 0);

    return {
      labels: items.map((i) => i.label),
      data: items.map((i) => i.value),
      colors: items.map((i) => i.color),
    };
  }, [
    dashboardData.totalScope1Emissions,
    dashboardData.totalScope2Emissions,
    dashboardData.totalScope3Emissions,
  ]);

  const emissionByTypeChart = useMemo(() => {
    const scopeFieldMap = {
      scope1: "scope1EmissionsMetricTon",
      scope2: "scope2EmissionsMetricTon",
      scope3: "scope3EmissionsMetricTon",
    };
    const scopeField = scopeFieldMap[selectedEmissionScope] || scopeFieldMap.scope1;

    const grouped = franchiseRecords.reduce((acc, item) => {
      const type = String(item?.franchiseType || "").trim() || "Unknown";
      const emission = getNumeric(item?.[scopeField]);
      acc[type] = (acc[type] || 0) + emission;
      return acc;
    }, {});

    const sorted = Object.entries(grouped)
      .map(([label, value]) => ({ label, value }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    return {
      labels: sorted.map((item) => item.label),
      values: sorted.map((item) => item.value),
    };
  }, [franchiseRecords, selectedEmissionScope]);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Franchises</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
            fixedPeriodMode: true,
            fromFixedLabel: "1st April",
            toFixedLabel: "31st March",
            fromFixedMonth: 4,
            toFixedMonth: 3,
          }}
        />
      </div>
      <div className={styles.purchase_container} style={{ paddingTop: "0.5rem" }}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Total Emissions (tCO2e)</p>
          <p className={styles.graph_value}>
            {formatNumberCommaSeparated(fixedTons(totalEmission))}
          </p>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: "1rem",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <div
            className={styles.card_container}
            style={{
              width: "100%",
              minWidth: 0,
            }}
          >
            <p className={styles.graph_heading}>Emission Split</p>
            {/* <p className={styles.graph_value}>
              {formatNumberCommaSeparated(fixedTons(totalEmission))}
            </p> */}
            <div
              style={{
                width: "100%",
                marginTop: "0.5rem",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "280px",
                  aspectRatio: "1",
                  margin: "0 auto",
                }}
              >
                {hasData ? (
                  <PieChart
                    labels={pieChartConfig.labels}
                    customHeight="100%"
                    customWidth="100%"
                    labelMode="label"
                    showBorder={false}
                    dataset={[
                      {
                        data: pieChartConfig.data,
                        backgroundColor: pieChartConfig.colors,
                      },
                    ]}
                    hide={false}
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NoDataFound mt={0} />
                  </div>
                )}
              </div>
            </div>
            {hasData ? (
              <div
                style={{
                  marginTop: "0.75rem",
                  width: "100%",
                  maxWidth: "520px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {visibleScopePercentageRows.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          display: "inline-block",
                        }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        {item.label}
                      </span>
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {item.percentage}%
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div
            style={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              height: "100%",
            }}
          >
            <div
              className={styles.card_container}
              style={{
                minHeight: "110px",
                maxHeight: "140px",
                width: "100%",
                borderRadius: "18px",
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <p className={styles.graph_heading}>Total Franchises</p>
              <p className={styles.graph_value}>
                {formatNumberCommaSeparated(fixedTons(dashboardData.totalFranchises))}
              </p>
            </div>
            <div
              className={styles.card_container}
              style={{
                minHeight: "180px",
                width: "100%",
                borderRadius: "18px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <p className={styles.graph_heading}>Number of Franchises by Type</p>
              <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
                {franchiseTypeCounts.length > 0 ? (
                  franchiseTypeCounts.map((item) => (
                    <div
                      key={item.type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>{item.type}</span>
                      <span style={{ fontSize: "14px", fontWeight: 600 }}>
                        {String(item.count).padStart(2, "0")}
                      </span>
                    </div>
                  ))
                ) : (
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    No franchise categories found
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <p className={styles.graph_heading}>Franchise type wise emission</p>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedEmissionScope}
                onChange={(e) => setSelectedEmissionScope(e.target.value)}
              >
                <MenuItem value="scope1">Scope 1</MenuItem>
                <MenuItem value="scope2">Scope 2</MenuItem>
                <MenuItem value="scope3">Scope 3</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <BarChart
              labels={emissionByTypeChart.labels}
              datasets={[
                {
                  label: "",
                  data: emissionByTypeChart.values,
                  backgroundColor: "#EF8E10",
                },
              ]}
              indexAxis="y"
              customHeight="360px"
              horizontalCleanStyle
              valueAxisTitle="(Tco2e)"
              ellipsis
              linearTickFormatter={(value) => formatNumberCommaSeparated(fixedTons(value))}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
